import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UrlSerializer } from '@angular/router';
import { ApiModule } from '@api/api.module';
import { Configuration } from '@api/configuration';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalService } from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import 'moment/locale/de.js';
import { ToastrModule } from 'ngx-toastr';
import { environment } from './../environments/environment';
import { App } from './app';
import { AppRoutingModule } from './app-routing.module';
import { APP_NAME, MSALGuardConfigFactory, MSALInitializeFactory, MSALInstanceFactory, MSALInterceptorConfigFactory, TENANT_ID, baseUrlFactory, queryTenantId } from './authorization/authorization-factories';
import { AuthorizationService } from './authorization/authorization.service';
import { NotAuthorizedComponent } from './components/not-authorized/not-authorized.component';
import { NotFound } from './components/not-found/not-found';
import { CustomUrlSerializer } from './shared/services/custom-url-serializer';
import { HttpErrorInterceptor } from './shared/services/http-error-interceptor.service';
import { SharedModule } from './shared/shared.module';
import { ApplicationState } from './shared/state/application.state';
import { PressureState } from './shared/state/pressure/pressure.state';

const tenantId = queryTenantId();
const appName = 'pressureMonitoring';

@NgModule({
    declarations: [App, NotFound, NotAuthorizedComponent],
    imports: [
        MsalModule,
        BrowserAnimationsModule,
        BrowserModule,
        SharedModule.forRoot(),
        NgbModule,
        FormsModule,
        ReactiveFormsModule,
        ToastrModule.forRoot(),
        ApiModule.forRoot(
            () =>
                new Configuration({
                    basePath: environment.basePath,
                })
        ),
        // ! NGXS must be initialised for root before other feature state modules.
        NgxsModule.forRoot([ApplicationState, PressureState], {
            developmentMode: !environment.production,
            compatibility: {
                strictContentSecurityPolicy: true, // needed for content security policy
            },
        }),
        NgxsStoragePluginModule.forRoot({
            keys: [ApplicationState, PressureState],
        }),
        NgxsRouterPluginModule.forRoot(),
        NgxsLoggerPluginModule.forRoot({ disabled: !environment.showNgxsLogs }),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
        // ! This needs to stay at the end of the list, in case other modules
        // ! are added that use their own routing.
        AppRoutingModule,
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        // HTTP interceptor is needed for MSAL to add authentication header while sending requests to backend
        provideAppInitializer(() => {
            const msalInstance = inject(MSAL_INSTANCE) as unknown as IPublicClientApplication;
            return MSALInitializeFactory(msalInstance)();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        {
            provide: TENANT_ID,
            useValue: tenantId,
        },
        {
            provide: APP_NAME,
            useValue: appName,
        },
        {
            provide: APP_BASE_HREF,
            useFactory: baseUrlFactory,
        },
        { provide: UrlSerializer, useClass: CustomUrlSerializer },
        AuthorizationService,
        MsalService,
        MsalGuard,
        MsalBroadcastService,
    ],
    bootstrap: [App],
})
export class AppModule {}
